// The bean library (concept beans-and-shot-history §4 B and C, variant B decided 19.10.2026): every bean the cloud
// keeps, the current one first, with its newest recipe, active bag and shots; one bean with its bags, recipe versions
// (what changed, who, when) and earlier names. The cloud owns all of it; the machine holds only the current recipe.
import type { BeanBag, BeanItem, BeanRecipe, BeanRecipeVersion, ShotItem } from '@brewcore/app-contract';
import type { BeansView } from '../beans.js';
import type { ShotsView } from '../shots.js';
import type { Bean } from '../recipe.js';
import { bagLeftG } from './brew.js';
import type { ScreenContext } from './context.js';
import {
  dotJoin,
  formatAuto,
  formatNumber,
  lastDayText,
  metaOf,
  shotMs,
  shotTime,
  whenText,
} from './format.js';

/** A local day (days since 1970-01-01) as "01.10." (this year) or "01.10.2025". */
export function calendarDayText(day: number, ctx: ScreenContext): string {
  const loc = ctx.lang === 'de' ? 'de-DE' : 'en-GB';
  const d = new Date(day * 86_400_000);
  const sameYear = d.getUTCFullYear() === new Date(ctx.today * 86_400_000).getUTCFullYear();
  return d.toLocaleDateString(loc, {
    day: '2-digit',
    month: '2-digit',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  });
}

/** "Tag 9" of a shot or today: the local day minus the roast day; null without a roast day. */
export const dayAfterRoast = (roastDay: number | null | undefined, day: number): number | null =>
  typeof roastDay === 'number' ? Math.max(0, day - roastDay) : null;

/** A recipe of the library in the shape the brew tab summarises ("18 g → 36 g · Mahlgrad 2,4 · 93 °C · PI"). */
export function asBean(recipe: BeanRecipe | null, roastDay: number | null = null): Bean {
  return {
    id: 0,
    name: '',
    ...(recipe
      ? {
          dose: recipe.dose,
          ...(recipe.grind !== null ? { grind: recipe.grind } : {}),
          setpoint: recipe.setpoint,
          tempEnd: recipe.tempEnd,
          ...(recipe.stopMode !== null ? { stopMode: recipe.stopMode } : {}),
          targetTime: recipe.targetTime,
          targetWeight: recipe.targetWeight,
          preinfusion: recipe.preinfusion,
        }
      : {}),
    roastDay,
  };
}

// ------------------------------------------------------------------------------------------------ list

export interface LibraryRow {
  id: string;
  name: string;
  /**
   * The roaster with the name (Dominik 23.10.2026: "rosterei und cafe bohnenname zusammen anzeigen"); in the list it
   * drops out of the second line when that line has no room (WRITING.md 0a, never cut with "…")
   */
  roaster: string | null;
  /** "Tag 11 · ~180 g" (open), "58 Bezüge" (archive); '' when nothing is known (no "ohne Röstdatum") */
  state: string;
  /** the day of the last shot ("heute", "07.10."), at the right of the name like a shot's time; null without shots */
  last: string | null;
  /** the bean in the grinder */
  marker: { text: string; tone: 'accent' | 'neutral' } | null;
}

export type LibraryTab = 'open' | 'archive';

export interface LibraryView {
  title: string;
  tabs: { value: LibraryTab; label: string; on: boolean }[];
  loading: boolean;
  empty: string | null;
  error: string | null;
  items: LibraryRow[];
  add: string;
  /** archive: each row brings its bean back */
  unarchive: string | null;
}

/** "Ethiopia Guji · The Barn": the bean with its roaster wherever it stands on one line (not in name checks). */
export const beanLabel = (b: Pick<BeanItem, 'name' | 'roaster'>): string =>
  dotJoin([b.name, b.roaster?.trim() ?? '']);

const roasterOf = (b: BeanItem): string | null => (b.roaster?.trim() ? b.roaster.trim() : null);

/** "Tag 11 · ~180 g": freshness and what is left of the open bag, each only when known. */
function bagState(ctx: ScreenContext, b: BeanItem, sv?: ShotsView): string {
  const day = dayAfterRoast(b.bag?.roastDay, ctx.today);
  const left = bagLeftG(b, sv);
  return dotJoin([
    day !== null ? ctx.tr('dayN', { n: day }) : '',
    left !== null ? ctx.tr('bagLeft', { g: left }) : '',
  ]);
}

/**
 * A bean of the library in two lines that never wrap (Dominik 23.10.2026: "Maximal 2 Zeilen ohne Umbruch"): the name
 * with "In der Mühle", the day of its last shot; below the roaster, the day after roasting and what is left.
 */
export function libraryRow(ctx: ScreenContext, b: BeanItem, sv?: ShotsView): LibraryRow {
  return {
    id: b.id,
    name: b.name,
    roaster: roasterOf(b),
    state: b.archivedAt
      ? b.shotCount
        ? ctx.tr(b.shotCount === 1 ? 'beanShotOne' : 'beanShots', { n: b.shotCount })
        : ''
      : bagState(ctx, b, sv),
    last: b.lastShotAt ? lastDayText(b.lastShotAt, ctx.lang, ctx.now) : null,
    // the bean in the grinder in the light pill of the board ("Bohnen"), not the green of ready
    marker: !b.archivedAt && b.current ? { text: ctx.tr('beanActive'), tone: 'accent' } : null,
  };
}

/** The active beans (the current one first) or the archive; archived beans stay in the history and its filter. */
export function libraryView(
  ctx: ScreenContext,
  bv: BeansView,
  tab: LibraryTab = 'open',
  sv?: ShotsView,
): LibraryView {
  const open = bv.items.filter((b) => !b.archivedAt);
  const archived = bv.items.filter((b) => b.archivedAt);
  const shown = tab === 'open' ? open : archived;
  return {
    title: ctx.tr('beansTitle'),
    tabs: [
      { value: 'open', label: ctx.tr('beansOpen', { n: open.length }), on: tab === 'open' },
      { value: 'archive', label: ctx.tr('beansArchive', { n: archived.length }), on: tab === 'archive' },
    ],
    loading: !bv.loaded && bv.loading,
    empty:
      bv.loaded && shown.length === 0 ? ctx.tr(tab === 'open' ? 'beansEmpty' : 'beansArchiveEmpty') : null,
    error: bv.error,
    items: shown.map((b) => libraryRow(ctx, b, sv)),
    add: ctx.tr('beanAdd'),
    unarchive: tab === 'archive' ? ctx.tr('beanUnarchive') : null,
  };
}

/** A name typed for a new or renamed bean: at most 40 bytes, not twice among the open beans (case does not matter). */
export function beanNameCheck(
  ctx: ScreenContext,
  name: string,
  bv: BeansView,
  except?: string,
): { ok: boolean; error: string | null } {
  const n = name.trim();
  if (!n) return { ok: false, error: null };
  if (new TextEncoder().encode(n).length > 40) return { ok: false, error: ctx.tr('beanLong') };
  if (
    bv.items.some((b) => b.id !== except && !b.archivedAt && b.name.trim().toLowerCase() === n.toLowerCase())
  )
    return { ok: false, error: ctx.tr('beanExists') };
  return { ok: true, error: null };
}

// ------------------------------------------------------------------------------------------------ one bean

/** What changed from one recipe version to the next ("Mahlgrad 2,4 → 2,3"); the lag is not part of a version. */
export function recipeChanges(ctx: ScreenContext, before: BeanRecipe, after: BeanRecipe): string[] {
  const n = (v: number | null, d?: number) =>
    v === null ? '–' : d === undefined ? formatAuto(v, ctx.lang) : formatNumber(v, d, ctx.lang);
  const stop = (m: BeanRecipe['stopMode']) =>
    m === null ? '–' : ctx.tr(m === 'hand' ? 'stopHand' : m === 'time' ? 'stopByTime' : 'stopByWeight');
  const pi = (p: BeanRecipe['preinfusion']) =>
    !p ? '–' : p.on ? `${n(p.burst, 1)} + ${n(p.pause, 1)} s` : ctx.tr('off');
  const out: string[] = [];
  const push = (label: string, a: string, b: string) => {
    if (a !== b) out.push(`${label} ${a} → ${b}`);
  };
  push(ctx.tr('dose'), `${n(before.dose)} g`, `${n(after.dose)} g`);
  push(ctx.tr('grindLabel'), before.grind ?? '–', after.grind ?? '–');
  // the unit says what it is (board "Eine Bohne": "Mahlgrad 3 → 2,6 · 92 °C → 93 °C")
  if (n(before.setpoint) !== n(after.setpoint))
    out.push(`${n(before.setpoint)} °C → ${n(after.setpoint)} °C`);
  push(ctx.tr('tempEnd'), `${n(before.tempEnd, 1)} K`, `${n(after.tempEnd, 1)} K`);
  push(ctx.tr('stop'), stop(before.stopMode), stop(after.stopMode));
  push(ctx.tr('targetTime'), `${n(before.targetTime)} s`, `${n(after.targetTime)} s`);
  push(ctx.tr('targetWeight'), `${n(before.targetWeight, 1)} g`, `${n(after.targetWeight, 1)} g`);
  push(ctx.tr('sPreinf'), pi(before.preinfusion), pi(after.preinfusion));
  return out;
}

export interface VersionRow {
  id: string;
  n: number;
  /** "Mahlgrad 2,6 → 2,5", "erster Stand"; '' when the version before is not known */
  change: string;
  /** "Version 4" */
  label: string;
  /** "19.10." */
  when: string;
  current: boolean;
}

export interface BagRow {
  id: string;
  /** "Röstung 09.10.", or "Packung" without a roast date */
  label: string;
  /** "Tag 11 · ~180 von 250 g" (open), "leer seit 08.10." */
  text: string;
  active: boolean;
  /** "Leer" on an open bag; an empty bag is history without a button (Dominik 23.10.2026) */
  empty: string | null;
}

export interface BeanFigure {
  key: 'shots' | 'good' | 'time';
  label: string;
  value: string;
}

/** The figures of a bean from the cloud: its shots, how many of them balanced, their mean time (s). */
export interface BeanFigures {
  count: number;
  good: number;
  time: number | null;
}

export interface BeanPageView {
  title: string;
  /** under the name: the roaster */
  sub: string | null;
  marker: { text: string; tone: 'accent' | 'neutral' } | null;
  /** Bezüge, ausgewogen, Ø Zeit; empty without shots */
  figures: BeanFigure[];
  /** "Äthiopien · gewaschen" */
  origin: { label: string; value: string } | null;
  /** the bean's reference shot (a row that opens it); null without one */
  reference: { label: string; info: string; text: string; shotId: string } | null;
  notes: string | null;
  /** the newest recipe as figures; "Version 4 · seit 19.10." beside the title */
  recipe: { title: string; version: string; facts: { key: string; label: string; value: string }[] } | null;
  bags: { title: string; items: BagRow[]; empty: string };
  /** the earlier versions behind one row (Dominik 21.10.2026), shown from two versions on */
  versions: { title: string; count: string; items: VersionRow[] } | null;
  /** detail still loading */
  loading: boolean;
  /** "Wählen" (null for the current bean), archive or bring back, delete (only without shots, never the current) */
  actions: {
    select: string | null;
    archive: { label: string; archived: boolean } | null;
    /** deleting: the row's label, the question as the title, its text, the button (WRITING.md 2) */
    remove: { label: string; title: string; ask: string; ok: string } | null;
    edit: string;
    addBag: string;
  };
}

function versionChange(
  ctx: ScreenContext,
  v: BeanRecipeVersion,
  before: BeanRecipeVersion | undefined,
): string {
  // without the version before (the list carries only the newest) nothing can be said about what changed
  if (before) return dotJoin(recipeChanges(ctx, before.recipe, v.recipe)) || ctx.tr('beanSameVersion');
  return v.n === 1 ? ctx.tr('beanFirstVersion') : '';
}

function bagRow(ctx: ScreenContext, bean: BeanItem, g: BeanBag, active: boolean, sv?: ShotsView): BagRow {
  const label =
    g.roastDay !== null ? `${ctx.tr('bagRoast')} ${calendarDayText(g.roastDay, ctx)}` : ctx.tr('bagAdd');
  if (g.emptiedAt)
    return {
      id: g.id,
      label,
      text: ctx.tr('bagEmptySince', { d: calendarDayText(Math.floor(g.emptiedAt / 86_400_000), ctx) }),
      active: false,
      empty: null,
    };
  const day = active ? dayAfterRoast(g.roastDay, ctx.today) : null;
  const left = active ? bagLeftG(bean, sv) : null;
  const weight = g.weightG ? formatAuto(g.weightG, ctx.lang) : null;
  return {
    id: g.id,
    label,
    text: dotJoin([
      day !== null ? ctx.tr('dayN', { n: day }) : '',
      left !== null && weight
        ? ctx.tr('bagLeftOf', { g: left, w: weight })
        : left !== null
          ? ctx.tr('bagLeft', { g: left })
          : weight
            ? `${weight} g`
            : '',
    ]),
    active,
    empty: ctx.tr('bagEmpty'),
  };
}

/** The newest recipe as the figures of the board "Eine Bohne": dose, stop, ratio, grind, temperature, lag. */
function recipeFacts(
  ctx: ScreenContext,
  v: BeanRecipeVersion,
): { key: string; label: string; value: string }[] {
  const r = v.recipe;
  const n = (x: number, d?: number) =>
    d === undefined ? formatAuto(x, ctx.lang) : formatNumber(x, d, ctx.lang);
  const fact = (key: string, label: string, value: string | null) => (value ? [{ key, label, value }] : []);
  const stop =
    r.stopMode === 'weight' && r.targetWeight
      ? `${n(r.targetWeight)} g`
      : r.stopMode === 'time' && r.targetTime
        ? `${n(r.targetTime)} s`
        : r.stopMode === 'hand'
          ? ctx.tr('stopHand')
          : null;
  const ratio =
    r.stopMode === 'weight' && r.targetWeight && r.dose
      ? `1:${n(Math.round((r.targetWeight / r.dose) * 10) / 10)}`
      : null;
  return [
    ...fact('dose', ctx.tr('dose'), r.dose ? `${n(r.dose)} g` : null),
    ...fact('stop', ctx.tr('shotStop'), stop),
    ...fact('ratio', ctx.tr('ratio'), ratio),
    ...fact('grind', ctx.tr('grindLabel'), r.grind),
    ...fact('temp', ctx.tr('brewTemp'), r.setpoint ? `${n(r.setpoint)} °C` : null),
    ...fact('lag', ctx.tr('lag'), v.lag !== null ? `${formatNumber(v.lag, 2, ctx.lang)} s` : null),
    ...fact(
      'pi',
      ctx.tr('sPreinf'),
      r.preinfusion?.on ? `${n(r.preinfusion.burst, 1)} + ${n(r.preinfusion.pause, 1)} s` : null,
    ),
  ];
}

/**
 * One bean (board "Eine Bohne", from the list until its detail arrived): the roaster under the name, the figures, the
 * origin and the reference; the bags with "Leer" on the open one; the newest recipe as figures with its earlier
 * versions folded behind one row. `refShot` is its reference shot once loaded, `figures` the cloud's figures.
 */
export function beanPage(
  ctx: ScreenContext,
  bean: BeanItem,
  refShot?: ShotItem | null,
  figures?: BeanFigures | null,
  sv?: ShotsView,
): BeanPageView {
  const refId = bean.referenceShotId ?? null;
  const versions = bean.recipes ?? (bean.recipe ? [bean.recipe] : []);
  const bags = bean.bags ?? (bean.bag ? [bean.bag] : []);
  const active = bags.find((g) => !g.emptiedAt);
  const count = figures?.count ?? bean.shotCount;
  const origin = dotJoin([bean.origin?.trim() ?? '', bean.process?.trim() ?? '']);
  const refText = refShot
    ? dotJoin([
        whenText(shotMs(refShot), ctx.lang, ctx.now),
        `${formatNumber(shotTime(metaOf(refShot)).main, 0, ctx.lang)} s`,
        typeof metaOf(refShot).weight === 'number'
          ? `${formatNumber(metaOf(refShot).weight as number, 1, ctx.lang)} g`
          : '',
      ])
    : '…';
  return {
    title: bean.name,
    sub: roasterOf(bean),
    marker: bean.archivedAt
      ? { text: ctx.tr('beanArchived'), tone: 'neutral' }
      : bean.current
        ? { text: ctx.tr('beanActive'), tone: 'accent' }
        : null,
    figures:
      count > 0
        ? [
            { key: 'shots', label: ctx.tr('beanShotsLabel'), value: String(count) },
            ...(figures
              ? [{ key: 'good' as const, label: ctx.tr('good'), value: String(figures.good) }]
              : []),
            ...(figures?.time !== null && figures?.time !== undefined
              ? [
                  {
                    key: 'time' as const,
                    label: ctx.tr('avgTime'),
                    value: `${formatNumber(figures.time, 0, ctx.lang)} s`,
                  },
                ]
              : []),
          ]
        : [],
    origin: origin ? { label: ctx.tr('origin'), value: origin } : null,
    reference: refId
      ? { label: ctx.tr('beanReference'), info: ctx.tr('beanReferenceInfo'), text: refText, shotId: refId }
      : null,
    notes: bean.notes,
    recipe: bean.recipe
      ? {
          title: ctx.tr('recipeTitle'),
          version: ctx.tr('beanVersionSince', {
            n: bean.recipe.n,
            d: lastDayText(bean.recipe.seenAt, ctx.lang, ctx.now),
          }),
          facts: recipeFacts(ctx, bean.recipe),
        }
      : null,
    bags: {
      title: ctx.tr('bags'),
      items: bags.map((g) => bagRow(ctx, bean, g, g === active, sv)),
      empty: ctx.tr('bagsNone'),
    },
    versions:
      versions.length > 1
        ? {
            title: ctx.tr('beanVersions'),
            count: String(versions.length),
            items: versions.map((v, i) => ({
              id: v.id,
              n: v.n,
              change: versionChange(ctx, v, versions[i + 1]),
              label: ctx.tr('beanVersionN', { n: v.n }),
              when: lastDayText(v.seenAt, ctx.lang, ctx.now),
              current: i === 0,
            })),
          }
        : null,
    loading: bean.recipes === undefined,
    actions: {
      select: bean.current ? null : ctx.tr('beanUse'),
      archive: bean.current
        ? null
        : { label: ctx.tr(bean.archivedAt ? 'beanUnarchive' : 'beanArchive'), archived: !bean.archivedAt },
      remove:
        bean.current || bean.shotCount > 0
          ? null
          : {
              label: ctx.tr('beanDelete'),
              title: ctx.tr('beanDeleteQ'),
              ask: ctx.tr('reallyDeleteLibraryBean', { n: bean.name }),
              ok: ctx.tr('deleteGo'),
            },
      edit: ctx.tr('beanEdit'),
      addBag: ctx.tr('bagAdd'),
    },
  };
}
